import React, { useEffect, useState } from 'react'
import Header from '../../../components/Headers/Header'
import CharCheckModal from '../../../components/molecules/CharCheckModal'
import {
  ClientName,
  FileSelector,
  LanguageCode,
  ReportName,
  SelectDimensionGroups,
  SelectDimensions,
  SelectMetrics,
} from '../ReportField/ReportField'
import { extractData, notify, prefix } from '../../../utils/index_tagging_tool'
import { api, BASE_URL } from '../../../utils/index'
import { Button, Card, CardBody, CardHeader, Col, Form, Row } from 'reactstrap'
import SetTitle from '../../../utils/set_title'
import { useFileUpload } from '../../../hooks'

// Component for the report creation form, for SEOs
export default function CreateReport() {
  SetTitle('Tagging tool - Create Report');

  // --- State for API Data & Dropdown Options ---
  const [reports, setReports] = useState([]); // List of existing report names for validation
  const [groupsWithDimensions, setGroupsWithDimensions] = useState([]); // Master list of all groups with their nested dimensions
  const [groupOptions, setGroupOptions] = useState([]); // Options for the "Groups" dropdown
  const [availableDimensionOptions, setAvailableDimensionOptions] = useState([]); // Dynamically populated options for the "Dimensions" dropdown
  const [savedMetrics, setSavedMetrics] = useState([]); // Options for the "Metrics" dropdown

  // --- State for User's Form Selections ---
  const [name, setName] = useState('');
  const [file, setFile] = useState(null);
  const [client, setClient] = useState('');
  const [languageCode, setLanguageCode] = useState('it');
  const [selectedDimensions, setSelectedDimensions] = useState([]);
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [selectedMetrics, setSelectedMetrics] = useState([]);
  const [excluded, setExcluded] = useState([]);

  // --- State for UI and Submission Logic ---
  const { upload, loading } = useFileUpload();
  const [showCharCheck, setShowCharCheck] = useState(false);
  const [errCharList, setErrCharList] = useState([]);
  const [acceptedCharCheck, setAcceptedCharCheck] = useState(false);
  const [, setReportId] = useState(null);

  // --- Initial Data Fetching ---
  useEffect(() => {
    // Fetch all necessary data on component mount
    api.get(prefix + 'reports/').then(extractData).then(data => setReports(data.map(x => x.name.toLowerCase())));
    api.get(prefix + 'saved-metrics/').then(extractData).then(data => setSavedMetrics(data.map(x => ({ label: x.name, value: x.name }))));

    // **IMPROVED LOGIC**: Fetch all groups with their nested dimensions in one call
    api.get(prefix + 'dimension-groups/')
      .then(extractData)
      .then(data => {
        setGroupsWithDimensions(data); // Store the full data structure
        const options = data.map(g => ({ label: g.name, value: g.name }));
        setGroupOptions(options);
      })
      .catch(console.error);
  }, []); // Empty dependency array ensures this runs only once

  // --- Dynamic Population of Dimensions based on Group Selection ---
  useEffect(() => {
    if (selectedGroups.length === 0) {
      setAvailableDimensionOptions([]);
      // If no groups are selected, clear any individually selected dimensions from those groups
      setSelectedDimensions([]);
      return;
    }

    const selectedGroupNames = selectedGroups.map(g => g.value);

    // Filter the master list to get only the selected groups' data
    const relevantGroups = groupsWithDimensions.filter(g => selectedGroupNames.includes(g.name));

    // Create the options for the dimensions dropdown from the selected groups
    const dimensionOptions = relevantGroups.flatMap(group =>
      group.dimensions.map(dim => ({
        label: `${dim.display_name} (${group.name})`, // e.g., "Brand (Brand)"
        value: `${dim.display_name} (${group.name})`, // Unique value for React-select
        // Store metadata needed for submission
        originalName: dim.display_name,
        groupName: group.name
      }))
    );

    setAvailableDimensionOptions(dimensionOptions);

    // Clean up: remove any selected dimensions that are no longer in the available options
    setSelectedDimensions(currentSelected =>
      currentSelected.filter(selectedDim =>
        dimensionOptions.some(option => option.value === selectedDim.value)
      )
    );
  }, [selectedGroups, groupsWithDimensions]);


  // --- Form Submission and Handlers ---
  const handleSubmit = async (e) => {
    if (e) e.preventDefault();

    let formData = new FormData();
    formData.append('file', file);
    formData.append('name', name);
    formData.append('client', client);
    formData.append('languageCode', languageCode);
    formData.append('groups', selectedGroups.map(x => x.value));
    formData.append('dimensions', JSON.stringify(
      selectedDimensions.map(d => ({
        name: d.originalName,
        group: d.groupName,
      }))
    ));
    formData.append('metrics', selectedMetrics.map(x => x.value));
    formData.append('excluded', excluded.map(x => x.value));
    if (acceptedCharCheck) formData.append('accepted', acceptedCharCheck);

    try {
      const newReportId = await upload('post', prefix + 'reports/', formData);
      setReportId(newReportId);
      notify('Report creato con successo', 'success');
      // Redirect to the newly created report's configuration page
      window.location.href = `/reporting/reports/seo/${newReportId}`;
    } catch (err) {
      if (err.response?.data?.message?.includes('ERROR Encoding keywords')) {
        setErrCharList(err.response.data?.errors);
        setShowCharCheck(true);
      }
      console.error('Report creation failed:', err);
    }
  };

  const confirmEvaluation = (e) => {
    e.preventDefault();
    setShowCharCheck(false);
    setAcceptedCharCheck(true);
  };

  useEffect(() => {
    if (acceptedCharCheck) {
      handleSubmit();
    }
  }, [acceptedCharCheck]);

  return (
    <>
      <Header iconClass="fa-solid fa-tags fa-xl" title="Tagging Tool" />
      <CharCheckModal show={showCharCheck} onHide={() => setShowCharCheck(false)} onSubmit={confirmEvaluation} errCharList={errCharList} />
      <Row style={{ marginBottom: "100px" }}>
        <Card className="bg-secondary shadow flex">
          <CardHeader className="bg-white border-0">
            <Row className="align-items-center">
              <Col>
                <h1>
                  <i className="fa-regular fa-clock fa-sm" /> Crea un Nuovo Report
                  <a href={BASE_URL + '/docs/documentazione_seo/tagging-tool-wiki/'} target="_blank" rel="noopener noreferrer" style={{ paddingLeft: "10px" }}>
                    <i className="fa-solid fa-question-circle fa-xs"></i>
                  </a>
                </h1>
              </Col>
            </Row>
          </CardHeader>
          <CardBody className="pt-0 pt-md-4">
            <div id="AddStudiokw">
              <Form onSubmit={handleSubmit}>
                <Row>
                  <Col className="col-12 col-md-6">
                    <FileSelector file={file} setFile={setFile} create={true} />
                  </Col>
                  <Col className="col-12 col-md-6">
                    <ReportName
                      names={reports}
                      name={name}
                      setName={setName}
                      create={true}
                    />
                  </Col>
                </Row>
                <hr />
                <Row>
                  <Col className="col-12 col-md-6">
                    <ClientName
                      client={client}
                      setClient={setClient}
                      create={true}
                    />
                  </Col>
                  <Col className="col-12 col-md-6">
                    <LanguageCode
                      languageCode={languageCode}
                      setLanguageCode={setLanguageCode}
                      create={true}
                    />
                  </Col>
                </Row>
                <hr />
                <Row>
                  <Col className="col-12 col-md-6">
                    <SelectDimensionGroups
                      options={groupOptions}
                      groups={selectedGroups}
                      setGroups={setSelectedGroups}
                      create={true}
                    />
                  </Col>
                  <Col className="col-12 col-md-6">
                    <SelectDimensions
                      options={availableDimensionOptions}
                      dimensions={selectedDimensions}
                      setDimensions={setSelectedDimensions}
                      excluded={null}
                      setExcluded={null}
                      create={true}
                    />
                  </Col>
                  <Col className="col-12 col-md-6">
                    <SelectMetrics
                      options={savedMetrics}
                      metrics={selectedMetrics}
                      setMetrics={setSelectedMetrics}
                      create={true}
                    />
                  </Col>
                  <Col className="col-12 col-md-6">
                    <SelectDimensions
                      options={availableDimensionOptions}
                      dimensions={selectedDimensions}
                      setDimensions={setSelectedDimensions}
                      excluded={excluded}
                      setExcluded={setExcluded}
                      create={true}
                    />
                  </Col>
                </Row>
                <Row className="justify-content-center mt-4">
                  {loading ? (
                    <div className="spinner-border" role="status">
                      <span className="sr-only">Loading...</span>
                    </div>
                  ) : (
                    <Button type="submit" color="success" style={{ float: 'right' }}> Crea </Button>
                  )}
                </Row>
              </Form>
            </div>
          </CardBody>
        </Card>
      </Row>
    </>
  );
}
